/**
 * Canonical bodygraph layout — 9 centers, 36 channels, 64 gates.
 * Coordinates target a 400x640 viewBox and mirror the standard
 * bodygraph geometry used across Human Design literature.
 */

export type CenterKey =
  | "head" | "ajna" | "throat" | "g" | "ego" | "spleen" | "sacral" | "solar" | "root";

export type CenterDef = {
  key: CenterKey;
  label: string;
  x: number;
  y: number;
  r: number;
  hue: number;         // used by living palette
  gates: number[];     // gates that live on this center
  summary: string;
};

export const CENTERS: CenterDef[] = [
  { key: "head",   label: "Head",         x: 200, y:  70, r: 34, hue:  52, gates: [64, 61, 63],
    summary: "Pressure center. Inspiration, doubt, mental pressure to think through the unknown." },
  { key: "ajna",   label: "Ajna",         x: 200, y: 150, r: 34, hue:  60, gates: [47, 24, 4, 17, 43, 11],
    summary: "Awareness of the mind. Concepts, opinions, certainty patterns." },
  { key: "throat", label: "Throat",       x: 200, y: 240, r: 40, hue:  30, gates: [62, 23, 56, 16, 20, 31, 8, 33, 35, 12, 45],
    summary: "Manifestation. Speaking, action and metabolism of expression." },
  { key: "g",      label: "G-Center",     x: 200, y: 340, r: 40, hue: 140, gates: [7, 1, 13, 10, 25, 15, 46, 2],
    summary: "Identity, love, direction. The magnetic monopole that pulls us toward the geometry of our life." },
  { key: "ego",    label: "Ego / Heart",  x: 292, y: 356, r: 30, hue:   0, gates: [21, 40, 26, 51],
    summary: "Willpower, resource, tribe. Motor of promises kept." },
  { key: "spleen", label: "Spleen",       x:  90, y: 400, r: 34, hue: 200, gates: [48, 57, 44, 50, 32, 28, 18],
    summary: "Intuition, immunity, time. Our oldest awareness, spontaneous and in the now." },
  { key: "sacral", label: "Sacral",       x: 200, y: 440, r: 40, hue:  15, gates: [5, 14, 29, 59, 9, 3, 42, 27, 34],
    summary: "Life-force motor. Generative energy, sexuality, sustainable work." },
  { key: "solar",  label: "Solar Plexus", x: 300, y: 440, r: 38, hue: 290, gates: [6, 37, 22, 36, 30, 55, 49],
    summary: "Emotional wave. Motor and (evolving) awareness through feeling over time." },
  { key: "root",   label: "Root",         x: 200, y: 550, r: 40, hue: 350, gates: [58, 38, 54, 53, 60, 52, 19, 39, 41],
    summary: "Pressure and adrenaline. Fuel for evolution and stress-driven action." },
];

export type ChannelDef = {
  gates: [number, number];
  ends: [CenterKey, CenterKey];
  name: string;
  circuit: "individual" | "tribal" | "collective";
};

/** All 36 channels of the bodygraph. */
export const CHANNELS: ChannelDef[] = [
  { gates: [64, 47], ends: ["head", "ajna"],    name: "Abstraction",       circuit: "collective" },
  { gates: [61, 24], ends: ["head", "ajna"],    name: "Awareness",         circuit: "individual" },
  { gates: [63,  4], ends: ["head", "ajna"],    name: "Logic",             circuit: "collective" },
  { gates: [17, 62], ends: ["ajna", "throat"],  name: "Acceptance",        circuit: "collective" },
  { gates: [43, 23], ends: ["ajna", "throat"],  name: "Structuring",       circuit: "individual" },
  { gates: [11, 56], ends: ["ajna", "throat"],  name: "Curiosity",         circuit: "collective" },
  { gates: [16, 48], ends: ["throat", "spleen"],name: "The Wavelength",    circuit: "collective" },
  { gates: [20, 57], ends: ["throat", "spleen"],name: "The Brainwave",     circuit: "individual" },
  { gates: [20, 10], ends: ["throat", "g"],     name: "Awakening",         circuit: "individual" },
  { gates: [20, 34], ends: ["throat", "sacral"],name: "Charisma",          circuit: "individual" },
  { gates: [31,  7], ends: ["throat", "g"],     name: "The Alpha",         circuit: "collective" },
  { gates: [8,   1], ends: ["throat", "g"],     name: "Inspiration",       circuit: "individual" },
  { gates: [33, 13], ends: ["throat", "g"],     name: "The Prodigal",      circuit: "collective" },
  { gates: [35, 36], ends: ["throat", "solar"], name: "Transitoriness",    circuit: "collective" },
  { gates: [12, 22], ends: ["throat", "solar"], name: "Openness",          circuit: "individual" },
  { gates: [45, 21], ends: ["throat", "ego"],   name: "Money Line",        circuit: "tribal"     },
  { gates: [10, 57], ends: ["g", "spleen"],     name: "Perfected Form",    circuit: "individual" },
  { gates: [10, 34], ends: ["g", "sacral"],     name: "Exploration",       circuit: "individual" },
  { gates: [25, 51], ends: ["g", "ego"],        name: "Initiation",        circuit: "individual" },
  { gates: [15,  5], ends: ["g", "sacral"],     name: "Rhythm",            circuit: "collective" },
  { gates: [46, 29], ends: ["g", "sacral"],     name: "Discovery",         circuit: "collective" },
  { gates: [2,  14], ends: ["g", "sacral"],     name: "The Beat",          circuit: "individual" },
  { gates: [7,  31], ends: ["g", "throat"],     name: "The Alpha II",      circuit: "collective" },
  { gates: [40, 37], ends: ["ego", "solar"],    name: "Community",         circuit: "tribal"     },
  { gates: [26, 44], ends: ["ego", "spleen"],   name: "Surrender",         circuit: "tribal"     },
  { gates: [50, 27], ends: ["spleen", "sacral"],name: "Preservation",      circuit: "tribal"     },
  { gates: [32, 54], ends: ["spleen", "root"],  name: "Transformation",    circuit: "tribal"     },
  { gates: [28, 38], ends: ["spleen", "root"],  name: "Struggle",          circuit: "individual" },
  { gates: [18, 58], ends: ["spleen", "root"],  name: "Judgment",          circuit: "collective" },
  { gates: [ 6, 59], ends: ["solar", "sacral"], name: "Mating",            circuit: "tribal"     },
  { gates: [22, 12], ends: ["solar", "throat"], name: "Openness II",       circuit: "individual" },
  { gates: [30, 41], ends: ["solar", "root"],   name: "Recognition",       circuit: "collective" },
  { gates: [55, 39], ends: ["solar", "root"],   name: "Emoting",           circuit: "individual" },
  { gates: [49, 19], ends: ["solar", "root"],   name: "Synthesis",         circuit: "tribal"     },
  { gates: [ 3, 60], ends: ["sacral", "root"],  name: "Mutation",          circuit: "individual" },
  { gates: [ 9, 52], ends: ["sacral", "root"],  name: "Concentration",     circuit: "collective" },
  { gates: [42, 53], ends: ["sacral", "root"],  name: "Maturation",        circuit: "collective" },
  { gates: [29, 46], ends: ["sacral", "g"],     name: "Discovery II",      circuit: "collective" },
];

export function centerByKey(k: CenterKey): CenterDef | undefined {
  return CENTERS.find((c) => c.key === k);
}

/** Position a gate as a small satellite around its center. */
export function gatePosition(center: CenterDef, gate: number): { x: number; y: number } {
  const idx = center.gates.indexOf(gate);
  const count = center.gates.length || 1;
  const angle = (idx / count) * Math.PI * 2 - Math.PI / 2;
  const rad = center.r + 14;
  return { x: center.x + Math.cos(angle) * rad, y: center.y + Math.sin(angle) * rad };
}
